import {
  useEventListener,
  useThrottledCallback,
  useWindowSize,
} from '@react-hookz/web';
import { useQuery } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  useLocation,
  useRouterState,
} from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getAuthTokeninfo } from '@/api/generated/digiVAPI';
import { DevTools } from '@/components/DevTools';
import { ModalManager } from '@/components/Modal/ModalManager';
import { Warning } from '@/components/Warning';
import { SolidSpinner } from '@/icons/components';
import { queryClient } from '@/services/queryClient';
import { useRefreshSession } from '@/stores/user';
import { Error404 } from '@/templates/Error404/Error404';
import { Error500 } from '@/templates/Error500/Error500';
import { twJoin } from '@/utils/tw-merge';

export const Route = createRootRouteWithContext<{
  queryClient: typeof queryClient;
  getTitle?: () => string;
}>()({
  notFoundComponent: () => <Error404 />,
  errorComponent: () => <Error500 />,
  component: function Root() {
    /**
     * Refresh the session
     */
    useRefreshSession();

    return (
      <div className="bg-gray-50">
        <Outlet />

        <PageLoader />

        <ModalManager />
        <DevTools />

        <EnvironmentBanner />
      </div>
    );
  },
});

const PageLoader = () => {
  const pathname = useLocation({ select: (location) => location.pathname });

  /**
   * Loading state
   */
  const previousRoute = useRef<string | null>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const isLoading = useRouterState({
    select: (s) => s.status === 'pending',
  });

  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    setShowLoading(false);

    if (previousRoute.current !== pathname) {
      if (timeout.current) clearTimeout(timeout.current);

      timeout.current = setTimeout(() => {
        setShowLoading(true);
      }, 500);
      previousRoute.current = pathname;
    }

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [pathname]);

  return (
    <AnimatePresence mode="wait">
      {showLoading && isLoading && (
        <motion.div
          key="pageloader"
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 100, y: 35 }}
          exit={{ opacity: 0, y: 0, transition: { delay: 0.3, duration: 0.3 } }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="absolute left-1/2 top-[50px] z-modal-above -ml-[40px] flex size-[80px] items-center justify-center rounded-full bg-black/80 text-white shadow-lg"
        >
          <SolidSpinner size={32} className="rotating" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const EnvironmentBanner = () => {
  // Fetch the deployment type from the auth token info, this property contains the current environment
  const environment = useQuery({
    queryKey: ['EnvironmentBanner'],
    queryFn: getAuthTokeninfo,
    select: (data) => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      deploymentType: (data as any)?.['deployment type'] as
        | 'ont'
        | 'int'
        | 'pre',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oefenomgeving: (data as any)?.oefenomgeving as boolean,
    }),
    staleTime: Infinity,
  });

  /**
   * When hovering over the warning, it needs to be hidden and elements below it need to be clickable.
   * Unfortunately, this is not possible with CSS, so we need to use a bit of JavaScript to achieve this.
   **/
  const [isHovered, setIsHovered] = useState(false);
  const [area, setArea] = useState<{
    fromBottom: number;
    fromLeft: number;
  } | null>(null);
  const windowSize = useWindowSize();
  const MARGIN = 50;

  // Store the warning area from the bottom left corner of the screen
  const measureAreaRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      const windowHeight = window.innerHeight;

      const { top, left, width } = node.getBoundingClientRect();

      setArea({
        fromLeft: left + width + MARGIN,
        fromBottom: windowHeight - top + MARGIN,
      });
    }
  }, []);

  /**
   * When the mouse moves, check if it is within the warning rectangle
   * We use a throttled callback to prevent the function from being called too often
   */
  const checkIfMouseIsWithinArea = useThrottledCallback(
    (event: MouseEvent) => {
      if (!area) return;

      const isWithinX = event.clientX < area.fromLeft;

      const distanceY = Math.abs(windowSize.height - event.clientY);
      const isWithinY = distanceY < area.fromBottom;

      // Hide warning if mouse is within range on both axes
      setIsHovered(isWithinX && isWithinY);
    },
    [area, windowSize],
    100
  );

  useEventListener(document, 'mousemove', checkIfMouseIsWithinArea);

  /**
   * Render the warning if it's not the production environment
   */
  if (!environment.data) {
    return null;
  }

  let label: string | null = null;

  if (environment.data.oefenomgeving) {
    label = 'Omgeving: Oefenomgeving';
  } else {
    switch (environment.data.deploymentType) {
      case 'ont':
        label = 'Omgeving: Ontwikkeling';
        break;
      case 'int':
        label = 'Omgeving: Integratie';
        break;
      case 'pre':
        label = 'Omgeving: Pre-productie';
        break;
    }
  }

  if (label) {
    return (
      <div
        ref={measureAreaRef}
        className={twJoin(
          'fixed bottom-3 left-3 z-modal-above',
          isHovered && 'hidden'
        )}
      >
        <Warning className="inline-block" variant="error" size="regular">
          <Warning.Title asChild>
            <p>{label}</p>
          </Warning.Title>
        </Warning>
      </div>
    );
  }
};
